import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import {
    UserIcon,
    BuildingOfficeIcon,
    BriefcaseIcon,
    ChartBarIcon,
    ClipboardDocumentListIcon,
    UsersIcon,
    CogIcon,
    EyeIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface StaffDashboardStats {
    tasks: {
        total: number;
        assigned: number;
        completed: number;
        pending: number;
    };
    users?: {
        total: number;
        active: number;
        newThisMonth: number;
    };
    staff?: {
        total: number;
        active: number;
    };
    applications?: {
        total: number;
        pending: number;
        approved: number;
    };
    inquiries?: {
        total: number;
        pending: number;
        contacted: number;
    };
}

interface RecentActivity {
    id: string;
    type: string;
    description: string;
    timestamp: string;
    status: string;
}

const StaffDashboard: React.FC = () => {
    const { user } = useAppSelector((state) => state.auth);
    const navigate = useNavigate();
    const [stats, setStats] = useState<StaffDashboardStats>({
        tasks: { total: 0, assigned: 0, completed: 0, pending: 0 }
    });
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            // Fetch tasks data
            const tasksResponse = await api.get('/tasks/user-tasks');
            const tasks = tasksResponse.data.data?.tasks || [];

            // Calculate stats based on staff role
            let dashboardStats: StaffDashboardStats = {
                tasks: {
                    total: tasks.length,
                    assigned: tasks.filter((task: any) => task.status === 'active').length,
                    completed: tasks.filter((task: any) => task.status === 'completed').length,
                    pending: tasks.filter((task: any) => task.status === 'pending').length
                }
            };

            // Add role-specific stats
            if (user?.role === 'recruiter') {
                const [usersResponse, staffResponse, applicationsResponse] = await Promise.all([
                    api.get('/users'),
                    api.get('/staff'),
                    api.get('/task-applications')
                ]);

                const users = usersResponse.data.data || [];
                const staff = staffResponse.data.data || [];
                const applications = applicationsResponse.data.data || [];

                dashboardStats = {
                    ...dashboardStats,
                    users: {
                        total: users.length,
                        active: users.filter((u: any) => u.isActive).length,
                        newThisMonth: users.filter((u: any) => {
                            const userDate = new Date(u.createdAt);
                            const now = new Date();
                            return userDate.getMonth() === now.getMonth() && userDate.getFullYear() === now.getFullYear();
                        }).length
                    },
                    staff: {
                        total: staff.length,
                        active: staff.filter((s: any) => s.isActive).length
                    },
                    applications: {
                        total: applications.length,
                        pending: applications.filter((a: any) => a.status === 'pending').length,
                        approved: applications.filter((a: any) => a.status === 'approved').length
                    }
                };
            }

            if (user?.role === 'crm') {
                const inquiriesResponse = await api.get('/inquiries');
                const inquiries = inquiriesResponse.data.data || [];

                dashboardStats = {
                    ...dashboardStats,
                    inquiries: {
                        total: inquiries.length,
                        pending: inquiries.filter((i: any) => i.status === 'pending').length,
                        contacted: inquiries.filter((i: any) => i.status === 'contacted').length
                    }
                };
            }

            setStats(dashboardStats);

            // Prepare recent activity
            const activity: RecentActivity[] = [
                ...tasks.slice(0, 3).map((task: any) => ({
                    id: task._id,
                    type: 'task',
                    description: `Task: ${task.title}`,
                    timestamp: task.createdAt,
                    status: task.status
                }))
            ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 5);

            setRecentActivity(activity);

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRoleTitle = () => {
        switch (user?.role) {
            case 'recruiter': return 'Recruiter Dashboard';
            case 'bdm': return 'Business Development Manager Dashboard';
            case 'project_trainer': return 'Project Trainer Dashboard';
            case 'crm': return 'Customer Relationship Manager Dashboard';
            default: return 'Staff Dashboard';
        }
    };

    const getRoleDescription = () => {
        switch (user?.role) {
            case 'recruiter': return 'Manage users, staff, and task applications';
            case 'bdm': return 'Manage tasks and validate data submissions';
            case 'project_trainer': return 'View and manage assigned tasks';
            case 'crm': return 'Manage customer inquiries and relationships';
            default: return 'Staff management dashboard';
        }
    };

    const getQuickActions = () => {
        const baseActions = [
            { name: 'View Tasks', icon: BriefcaseIcon, path: '/staff/tasks', color: 'bg-blue-500' }
        ];

        switch (user?.role) {
            case 'recruiter':
                return [
                    ...baseActions,
                    { name: 'User Management', icon: UsersIcon, path: '/staff/users', color: 'bg-green-500' },
                    { name: 'Staff Management', icon: CogIcon, path: '/staff/staff-management', color: 'bg-purple-500' },
                    // { name: 'Task Applications', icon: ClipboardDocumentListIcon, path: '/staff/task-applications', color: 'bg-indigo-500' }
                ];
            case 'bdm':
                return [
                    ...baseActions,
                    // { name: 'Data Validation', icon: CheckCircleIcon, path: '/staff/data-validation', color: 'bg-green-500' },
                    // { name: 'Task Analytics', icon: ChartBarIcon, path: '/staff/analytics', color: 'bg-purple-500' }
                ];
            case 'project_trainer':
                return [
                    ...baseActions,
                    { name: 'Assigned Tasks', icon: EyeIcon, path: '/staff/assigned-tasks', color: 'bg-green-500' }
                ];
            case 'crm':
                return [
                    ...baseActions,
                    { name: 'Inquiries', icon: ExclamationTriangleIcon, path: '/staff/inquiries', color: 'bg-red-500' },
                    { name: 'Customer Support', icon: UserIcon, path: '/staff/customer-support', color: 'bg-blue-500' }
                ];
            default:
                return baseActions;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{getRoleTitle()}</h1>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">{getRoleDescription()}</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Assigned Tasks */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <BriefcaseIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Assigned Tasks</dt>
                                    <dd className="text-lg font-medium text-gray-900 dark:text-white">{stats.tasks.assigned}</dd>
                                </dl>
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                                <span>Total: {stats.tasks.total}</span>
                                <span>Completed: {stats.tasks.completed}</span>
                            </div>
                        </div>
                    </div>

                    {/* Role-specific stats */}
                    {stats.users && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <UsersIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Users</dt>
                                        <dd className="text-lg font-medium text-gray-900 dark:text-white">{stats.users.total}</dd>
                                    </dl>
                                </div>
                            </div>
                            <div className="mt-4">
                                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                                    <span>Active: {stats.users.active}</span>
                                    <span>New: {stats.users.newThisMonth}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {stats.staff && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <CogIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Staff</dt>
                                        <dd className="text-lg font-medium text-gray-900 dark:text-white">{stats.staff.total}</dd>
                                    </dl>
                                </div>
                            </div>
                            <div className="mt-4">
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                    Active: {stats.staff.active}
                                </div>
                            </div>
                        </div>
                    )}

                    {stats.applications && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <ClipboardDocumentListIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Applications</dt>
                                        <dd className="text-lg font-medium text-gray-900 dark:text-white">{stats.applications.total}</dd>
                                    </dl>
                                </div>
                            </div>
                            <div className="mt-4">
                                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                                    <span>Pending: {stats.applications.pending}</span>
                                    <span>Approved: {stats.applications.approved}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {stats.inquiries && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Inquiries</dt>
                                        <dd className="text-lg font-medium text-gray-900 dark:text-white">{stats.inquiries.total}</dd>
                                    </dl>
                                </div>
                            </div>
                            <div className="mt-4">
                                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                                    <span>Pending: {stats.inquiries.pending}</span>
                                    <span>Contacted: {stats.inquiries.contacted}</span>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Quick Actions */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getQuickActions().map((action, index) => (
                            <button
                                key={index}
                                onClick={() => navigate(action.path)}
                                className={`${action.color} text-white p-4 rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-3`}
                            >
                                <action.icon className="h-6 w-6" />
                                <span className="font-medium">{action.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Activity</h2>
                    </div>
                    <div className="p-6">
                        {recentActivity.length > 0 ? (
                            <div className="space-y-4">
                                {recentActivity.map((activity) => (
                                    <div key={activity.id} className="flex items-center space-x-4">
                                        <div className="flex-shrink-0">
                                            <BriefcaseIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 dark:text-white">{activity.description}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {new Date(activity.timestamp).toLocaleString()}
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${activity.status === 'unread'
                                                ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                }`}>
                                                {activity.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No recent activity</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StaffDashboard;
